import { Box, Typography, Grid, Menu, MenuItem, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '../../../assets/ScheduleIcon/delete.svg';
import CalendarEditIcon from '../../../assets/ScheduleIcon/calendar-edit.svg';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { useState, useEffect } from 'react';
import './SessionAvailability.css';

// Import icons from assets
import OneOnOneIcon from '../../../assets/SessionIcon/one-on-one-session-icon.svg';
import GroupIcon from '../../../assets/ScheduleIcon/group-icon.svg';
import PrimeIcon from '../../../assets/SessionIcon/prime-session-icon.svg';
import SmartPauseIcon from '../../../assets/SessionIcon/smart-pause-icon.svg';
// Import DeleteAvailabilityModel
import DeleteAvailabilityModel from '../DeleteAvailability/DeleteAvailabilityModel';
// Import Edit Schedule Components
import EditOneOnOneSchedule from '../EditAvailability/EditOneOnOneSchedule/EditOneOnOneSchedule';
import EditGroupSchedule from '../EditAvailability/EditGroupSchedule/EditGroupSchedule';
import EditPrimeSchedule from '../EditAvailability/EditPrimeSchedule/EditPrimeSchedule';

// Level mapping for display
const levelMapping = {
  elementary: 'Elementary',
  high_school: 'High School',
  middle_school: 'Middle School',
  college_level: 'University',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  professional: 'Professional',
  All: 'All Level',
};

// Process sessions into displayable format
const processSessionsForDisplay = (sessions, pauseSessions = []) => {
  const displaySessions = [];

  // Group sessions by type and subject
  const sessionsByTypeAndSubject = {};

  // Process regular sessions
  sessions.forEach(session => {
    const key = `${session.session_type}-${session.subject}-${session.level_of_teaching}`;
    if (!sessionsByTypeAndSubject[key]) {
      sessionsByTypeAndSubject[key] = [];
    }
    sessionsByTypeAndSubject[key].push(session);
  });

  // Add smart pause sessions if available - create an individual card for each pause session
  if (pauseSessions.length > 0) {
    // Process each pause session individually instead of grouping them
    pauseSessions.forEach((pause, pauseIndex) => {
      // Create a unique key for each paused session
      const pauseKey = `smart_pause-All-All-${pause.id}`;
      sessionsByTypeAndSubject[pauseKey] = [
        {
          ...pause,
          session_type: 'smart_pause',
          subject: 'All',
          level_of_teaching: 'All',
        },
      ];
    });
  }

  // Create display objects
  Object.entries(sessionsByTypeAndSubject).forEach(([key, sessionGroup], index) => {
    const firstSession = sessionGroup[0];

    let icon, color, bgcolor, type;

    switch (firstSession.session_type) {
      case 'one_on_one':
        icon = OneOnOneIcon;
        color = '#6B60E7 !important';
        bgcolor = '#E8F0FF !important';
        type = 'One On One Session';
        break;
      case 'group':
        icon = GroupIcon;
        color = '#7BCF8C !important';
        bgcolor = '#FAFFFB !important';
        type = 'Group Session';
        break;
      case 'prime':
        icon = PrimeIcon;
        color = '#AD8B00 !important';
        bgcolor = '#FFF9E6 !important';
        type = 'Prime Session';
        break;
      case 'smart_pause':
        icon = SmartPauseIcon;
        color = '#1F435D !important';
        bgcolor = '#F0F0F0 !important';
        type = 'Paused Sessions';
        break;
      default:
        icon = CalendarMonthIcon;
        color = '#6B7280 !important';
        bgcolor = '#F0F0F0 !important';
        type = 'Unknown Session Type';
        break;
    }

    // Map level value to display text
    const levelValue = firstSession.level_of_teaching;
    const mappedLevel = levelMapping[levelValue] || levelValue;

    displaySessions.push({
      id: index + 1,
      type: type,
      sessionType: firstSession.session_type, // Add the raw session type for API purposes
      icon: icon,
      level: mappedLevel,
      levelOriginal: firstSession.level_of_teaching,
      subject: firstSession.subject === 'All' ? 'All Subject' : firstSession.subject,
      color: color,
      bgcolor: bgcolor,
      // Store the session data for easier access
      sessionData: firstSession,
    });
  });

  console.log('Processed session data:', displaySessions);
  return displaySessions;
};

const SessionAvailability = ({
  onEyeClick,
  sessionsData,
  pauseSessionsData,
  onDeleteAvailability,
}) => {
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State for menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  // State for delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // Track selected session ID for highlighting icons
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [previewSessionId, setPreviewSessionId] = useState(null);
  // State for edit modals
  const [editOneOnOneOpen, setEditOneOnOneOpen] = useState(false);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editPrimeOpen, setEditPrimeOpen] = useState(false);

  // Process data when props change
  useEffect(() => {
    try {
      // Process the data from props
      const formattedSessions = processSessionsForDisplay(
        sessionsData || [],
        pauseSessionsData || []
      );

      setSessionTypes(formattedSessions);
      setLoading(false);
    } catch (err) {
      console.error('Error processing availability data:', err);
      setError('Failed to process availability data');
      setLoading(false);
    }
  }, [sessionsData, pauseSessionsData]);

  // Handle opening the menu
  const handleMenuOpen = (event, session) => {
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
    setSelectedSessionId(session.id);
  };

  // Handle closing the menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    // Only clear selection if delete modal is not open
    if (!deleteModalOpen) {
      setSelectedSessionId(null);
    }
  };

  // Handle preview (eye icon) click
  const handlePreviewClick = session => {
    setPreviewSessionId(session.id);
    onEyeClick(session.sessionData);
  };

  // Handle edit availability
  const handleEditAvailability = () => {
    // Close the menu
    handleMenuClose();

    // Open the appropriate edit modal based on session type
    if (selectedSession) {
      const sessionType = selectedSession.sessionType;

      switch (sessionType) {
        case 'one_on_one':
          setEditOneOnOneOpen(true);
          break;
        case 'group':
          setEditGroupOpen(true);
          break;
        case 'prime':
          setEditPrimeOpen(true);
          break;
        default:
          console.log('Unknown session type for editing:', sessionType);
          break;
      }
    }
  };

  // Handle closing edit modals
  const handleCloseEditModal = () => {
    setEditOneOnOneOpen(false);
    setEditGroupOpen(false);
    setEditPrimeOpen(false);
    setSelectedSessionId(null);
  };

  // Handle delete availability
  const handleDeleteAvailability = () => {
    // Close the menu
    handleMenuClose();
    // Open delete modal
    setDeleteModalOpen(true);
  };

  // Handle closing the delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedSessionId(null);
  };

  // Handle deletion of availability
  const handleDeleteConfirm = session => {
    console.log('Availability deleted successfully:', session);

    // The actual API call and toast message is handled in the DeleteAvailabilityModel component

    // Remove from local state to update UI immediately after successful deletion
    setSessionTypes(prevTypes => prevTypes.filter(type => type.id !== session.id));

    // Close any open menus or modals
    setAnchorEl(null);
    setSelectedSessionId(null);

    // Call parent component's delete handler if provided
    if (onDeleteAvailability) {
      onDeleteAvailability(session);
    }

    // Notify parent to refresh calendar data - this is the new line
    if (window.refreshCalendarData) {
      window.refreshCalendarData();
    }
  };

  if (loading && (!sessionsData || sessionsData.length === 0)) {
    return (
      <Box className="session-availability">
        <CircularProgress sx={{ alignItems: 'center', justifyContent: 'center' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="session-availability">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Add check for empty sessions after loading is complete
  if (!loading && sessionTypes.length === 0) {
    return (
      <Box className="session-availability">
        <Box className="flex justify-between items-center mb-4">
          <Typography className="availability-text">Availability</Typography>
        </Box>
        <Box className="empty-availability-container">
          <CalendarMonthIcon className="empty-availability-icon" />
          <Typography className="empty-availability-text">No availability scheduled yet</Typography>
          <Typography className="empty-availability-subtext">
            Create new availability to see it here
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="session-availability">
      <Box className="flex justify-between items-center mb-4">
        <Typography className="availability-text">Availability</Typography>
      </Box>
      <Grid container spacing={2}>
        {sessionTypes.map((session, index) => (
          <Grid item xs={12} sm={6} key={session.id}>
            <Box className="session-card">
              <Box className="session-header">
                <Box className="session-type">
                  <Box className="session-icon-wrapper" sx={{ backgroundColor: session.bgcolor }}>
                    <img src={session.icon} alt={session.type} className="session-icon" />
                  </Box>
                  <Typography className="session-type-text" sx={{ color: session.color }}>
                    {session.type}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: '1px',
                    backgroundColor: session.color,
                    width: '100%',
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                  }}
                />
                <Box className="session-actions">
                  <VisibilityIcon
                    className={`action-icon eye ${previewSessionId === session.id ? 'active' : ''}`}
                    onClick={() => handlePreviewClick(session)}
                    sx={{
                      color:
                        previewSessionId === session.id
                          ? '#0a5ef3 !important'
                          : '#6b7280 !important',
                    }}
                  />
                  <SettingsIcon
                    className={`action-icon settings ${selectedSessionId === session.id ? 'active' : ''}`}
                    onClick={e => handleMenuOpen(e, session)}
                    sx={{
                      color:
                        selectedSessionId === session.id
                          ? '#0a5ef3 !important'
                          : '#6b7280 !important',
                    }}
                  />
                </Box>
              </Box>
              <Box className="session-details">
                <Box className="detail-row">
                  <Typography className="detail-label">Level</Typography>
                  <Typography className="detail-value" sx={{ color: '#6B60E7 !important' }}>
                    {session.level}
                  </Typography>
                </Box>
                <Box className="detail-row">
                  <Typography className="detail-label">Subject</Typography>
                  <Typography className="detail-value" sx={{ color: '#6B60E7 !important' }}>
                    {session.subject}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditAvailability} className="menu-item">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={CalendarEditIcon} alt="Edit" style={{ width: 20, height: 20 }} />
            <Typography>Edit Availability</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleDeleteAvailability} className="menu-item">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
            <Typography color="error">Delete Availability</Typography>
          </Box>
        </MenuItem>
      </Menu>

      {/* Delete Availability Modal */}
      {deleteModalOpen && (
        <DeleteAvailabilityModel
          open={deleteModalOpen}
          handleClose={handleCloseDeleteModal}
          session={selectedSession}
          onDelete={handleDeleteConfirm}
        />
      )}

      {/* Edit Availability Modals */}
      {editOneOnOneOpen && selectedSession && (
        <EditOneOnOneSchedule
          open={editOneOnOneOpen}
          onClose={handleCloseEditModal}
          availabilityId={selectedSession.sessionData.id}
        />
      )}

      {editGroupOpen && selectedSession && (
        <EditGroupSchedule
          open={editGroupOpen}
          onClose={handleCloseEditModal}
          availabilityId={selectedSession.sessionData.id}
        />
      )}

      {editPrimeOpen && selectedSession && (
        <EditPrimeSchedule
          open={editPrimeOpen}
          onClose={handleCloseEditModal}
          availabilityId={selectedSession.sessionData.id}
        />
      )}
    </Box>
  );
};

// Add this method to expose for parent components
SessionAvailability.closePreview = function (ref) {
  if (ref.current) {
    // Update the preview session ID state directly instead of calling a method
    if (ref.current.setPreviewSessionId) {
      ref.current.setPreviewSessionId(null);
    }
  }
};

export default SessionAvailability;
